/**
 * Lead Simulation Utility
 * 
 * DEV-ONLY: This module generates mock incoming leads for testing
 * the lead routing engine. Do NOT use in production.
 * 
 * NOTE: For production routing, use the Routing Orchestrator:
 * ```typescript
 * import { routeIncomingLead, routeNewLead } from '@/lib/routingOrchestrator';
 * ```
 * 
 * This module provides legacy simulation functions and mock lead generators.
 * New code should prefer the orchestrator for consistent routing behavior.
 */

import { routeLeadToAgent, type IncomingLead, type RoutingResult, type AgentStateForRouting, type PricingRuleGetter, type IntakeChecker, type AllocationWeightsForRouting } from './leadRouter';
import { routeLeadMultiAgent, type MultiAgentRoutingResult, type EligibilityContext } from './multiAgentRouter';
import { selectAgentPool, type PoolMeta } from './agentPoolSelector';
import { routeNewLead, type RoutingResult as OrchestratorResult } from './routingOrchestrator';
import type { MockAgent } from '@/contexts/MockAgentsContext';
import type { AllocationWeights } from '@/contexts/AllocationWeightsContext';
import type { AgentContractPressure } from '@/contexts/ContractsContext';
import type { GuardrailInputs } from './executionGuardrails';

// Re-export orchestrator for convenience
export { routeNewLead, routeIncomingLead, routeRetryLead } from './routingOrchestrator';
export type { RoutingResult as OrchestratedRoutingResult, RoutingContext, LeadInput } from './routingOrchestrator';

// --- Lead Type Definitions ---

export interface LeadTypeOption {
  id: string;
  name: string;
  vertical: 'life_insurance' | 'mortgage';
}

export const LEAD_TYPE_OPTIONS: LeadTypeOption[] = [
  { id: 'life_fex', name: 'Final Expense', vertical: 'life_insurance' },
  { id: 'life_iul', name: 'IUL', vertical: 'life_insurance' },
  { id: 'life_mp', name: 'Mortgage Protection', vertical: 'life_insurance' },
  { id: 'mortgage_purchase', name: 'Purchase', vertical: 'mortgage' },
  { id: 'mortgage_refi', name: 'Refinance', vertical: 'mortgage' },
  { id: 'mortgage_heloc', name: 'HELOC', vertical: 'mortgage' },
  { id: 'mortgage_reverse', name: 'Reverse', vertical: 'mortgage' },
  { id: 'mortgage_refi', name: 'Cash-Out', vertical: 'mortgage' },
];

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// --- Mock Data Generators ---

/**
 * Generate a unique lead ID
 */
function generateLeadId(): string {
  return `lead-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate a random estimated value for a lead
 */
function generateEstimatedValue(vertical: 'life_insurance' | 'mortgage'): number {
  if (vertical === 'life_insurance') {
    // Life insurance policies: $5,000 - $100,000
    return Math.floor(Math.random() * 95000) + 5000;
  } else {
    // Mortgage amounts: $100,000 - $800,000
    return Math.floor(Math.random() * 700000) + 100000;
  }
}

/**
 * Generate a mock incoming lead
 */
export function generateMockLead(leadTypeId: string, state: string): IncomingLead {
  const leadType = LEAD_TYPE_OPTIONS.find(lt => lt.id === leadTypeId);
  
  if (!leadType) {
    throw new Error(`Unknown lead type: ${leadTypeId}`);
  }
  
  return {
    id: generateLeadId(),
    leadTypeId: leadType.id,
    leadTypeName: leadType.name,
    vertical: leadType.vertical,
    state,
    estimatedValue: generateEstimatedValue(leadType.vertical),
  };
}

/**
 * Generate a completely random mock lead
 */
export function generateRandomLead(): IncomingLead {
  const randomLeadType = LEAD_TYPE_OPTIONS[Math.floor(Math.random() * LEAD_TYPE_OPTIONS.length)];
  const randomState = US_STATES[Math.floor(Math.random() * US_STATES.length)];
  
  return generateMockLead(randomLeadType.id, randomState);
}

// --- Simulation Function ---

export type SimulationResult = RoutingResult & {
  lead: IncomingLead;
  timestamp: string;
};

/**
 * Simulate an incoming lead through the routing engine
 * 
 * This is the main function for testing. It:
 * 1. Takes a lead and agent state
 * 2. Passes them through the routing engine (with optional pricing rules and intake check)
 * 3. Returns the result with additional metadata
 * 
 * The caller is responsible for applying side effects.
 */
export function simulateIncomingLead(
  lead: IncomingLead,
  agentState: AgentStateForRouting,
  getPricingRule?: PricingRuleGetter,
  checkIntake?: IntakeChecker,
  allocationWeights?: AllocationWeightsForRouting
): SimulationResult {
  const routingResult = routeLeadToAgent(lead, agentState, getPricingRule, checkIntake, allocationWeights);
  
  // Log priority score for debugging (dev only)
  if (routingResult.eligible === true) {
    console.log('[Lead Simulator] Priority Score:', routingResult.priorityScore);
    console.log('[Lead Simulator] Charged Amount:', routingResult.chargedAmount);
    console.log('[Lead Simulator] Allocation Breakdown:', routingResult.allocationBreakdown);
  } else if (routingResult.eligible === false) {
    console.log('[Lead Simulator] Rejected:', routingResult.reason);
    console.log('[Lead Simulator] Code:', routingResult.code);
  }
  
  return {
    ...routingResult,
    lead,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Quick simulation with auto-generated lead
 */
export function quickSimulate(
  leadTypeId: string,
  state: string,
  agentState: AgentStateForRouting,
  getPricingRule?: PricingRuleGetter,
  checkIntake?: IntakeChecker,
  allocationWeights?: AllocationWeightsForRouting
): SimulationResult {
  const lead = generateMockLead(leadTypeId, state);
  return simulateIncomingLead(lead, agentState, getPricingRule, checkIntake, allocationWeights);
}

// --- Multi-Agent Simulation ---

export type MultiAgentSimulationResult = MultiAgentRoutingResult & {
  lead: IncomingLead;
  timestamp: string;
  poolMeta?: PoolMeta;
};

/**
 * Simulate an incoming lead through the multi-agent routing engine
 * 
 * This evaluates ALL agents in the system for eligibility,
 * calculates priority scores, and uses weighted random selection
 * to pick a winner.
 */
export function simulateMultiAgentRouting(
  lead: IncomingLead,
  agents: MockAgent[],
  weights: AllocationWeights,
  context: EligibilityContext = {}
): MultiAgentSimulationResult {
  const routingResult = routeLeadMultiAgent(lead, agents, weights, context);
  
  // Log for debugging (dev only)
  if (import.meta.env.MODE !== "production") {
    if (routingResult.outcome === 'success') {
      console.log('[Multi-Agent Simulator] Winner:', routingResult.selectedAgentName);
      console.log('[Multi-Agent Simulator] Charged:', routingResult.chargedAmount);
      console.log('[Multi-Agent Simulator] Eligible Agents:', routingResult.eligibleAgents.length);
      console.log('[Multi-Agent Simulator] Pick Attempts:', routingResult.pickAttempts.length);
    } else {
      console.log('[Multi-Agent Simulator] UNSOLD:', routingResult.reason);
      console.log('[Multi-Agent Simulator] Evaluated:', routingResult.totalAgentsEvaluated);
    }
  }
  
  return {
    ...routingResult,
    lead,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Simulate with pool selection layer
 * 
 * This uses the agent pool selection layer to select a subset of agents
 * before running the routing engine. Scales better with 200+ agents.
 * 
 * @param contractPressures - Optional contract pressure data for agents
 * @param agentGuardrails - Optional execution guardrails from Delivery Control
 */
export function simulateWithPoolSelection(
  lead: IncomingLead,
  allAgents: MockAgent[],
  weights: AllocationWeights,
  context: EligibilityContext = {},
  targetPoolSize: number = 8,
  contractPressures?: Map<string, AgentContractPressure>,
  agentGuardrails?: Map<string, GuardrailInputs>
): MultiAgentSimulationResult {
  // Step 1: Select agent pool with goal-aware pacing and guardrails
  const poolResult = selectAgentPool({
    lead: {
      id: lead.id,
      leadTypeId: lead.leadTypeId,
      state: lead.state,
      vertical: lead.vertical,
    },
    allAgents,
    targetPoolSize,
    strategy: 'GOAL_AWARE_PACING', // Use goal-aware pacing by default
    agentGuardrails, // Pass guardrails to pool selection
    getPricingRule: context.getPricingRule,
    contractPressures,
  });
  
  // Log pool selection (dev only)
  if (import.meta.env.MODE !== "production") {
    console.log('[Pool Selection] Strategy:', poolResult.poolMeta.strategy);
    console.log('[Pool Selection] Pool Size:', poolResult.poolMeta.poolSize);
    console.log('[Pool Selection] Bucket:', `${poolResult.poolMeta.bucketIndex + 1}/${poolResult.poolMeta.totalBuckets}`);
    console.log('[Pool Selection] Pacing Boost:', poolResult.poolMeta.pacingBoostApplied);
    console.log('[Pool Selection] Pre-filtered:', `${poolResult.poolMeta.postFilteredCount}/${poolResult.poolMeta.preFilteredCount}`);
    
    // Log goal-aware pacing details
    if (poolResult.poolMeta.goalAwarePacing) {
      const gp = poolResult.poolMeta.goalAwarePacing;
      console.log('[Pool Selection] Goal-Aware:', `${gp.agentsBehindPace} behind, ${gp.agentsAheadOfPace} ahead`);
      console.log('[Pool Selection] Avg Deficit:', gp.averagePaceDeficit);
      console.log('[Pool Selection] Reason:', gp.poolSelectionReason);
      if (gp.dynamicPoolExpansion) {
        console.log('[Pool Selection] Dynamic Expansion: APPLIED');
      }
    }
  }
  
  // Step 2: Route within the selected pool with guardrails
  const contextWithGuardrails: EligibilityContext = {
    ...context,
    agentGuardrails, // Pass guardrails to routing engine for pacing caps
  };
  const routingResult = routeLeadMultiAgent(lead, poolResult.poolAgents, weights, contextWithGuardrails);
  
  // Step 3: Build pool meta with agent IDs and contract pressure
  const poolMetaWithIds: PoolMeta = {
    ...poolResult.poolMeta,
    poolAgentIds: poolResult.poolAgents.map(a => a.id),
  };
  
  // Add contract pressure metadata if available
  if (contractPressures && contractPressures.size > 0) {
    let totalCommitted = 0;
    let totalDelivered = 0;
    let totalUrgency = 0;
    let agentsWithContracts = 0;
    let agentsUnderPressure = 0;
    let atRiskContracts = 0;
    
    for (const agent of poolResult.poolAgents) {
      const pressure = contractPressures.get(agent.id);
      if (pressure && pressure.activeContracts > 0) {
        agentsWithContracts++;
        totalCommitted += pressure.totalCommitted;
        totalDelivered += pressure.totalDelivered;
        totalUrgency += pressure.contractPressureScore;
        atRiskContracts += pressure.atRiskContracts;
        if (pressure.contractPressureScore > 0.3) {
          agentsUnderPressure++;
        }
      }
    }
    
    const averageUrgencyScore = agentsWithContracts > 0 
      ? Math.round((totalUrgency / agentsWithContracts) * 1000) / 1000 
      : 0;
    
    poolMetaWithIds.contractPressure = {
      agentsWithContracts,
      agentsUnderPressure,
      totalCommitted,
      totalDelivered,
      averageUrgencyScore,
      atRiskContracts,
      contractPressureApplied: agentsUnderPressure > 0,
    };
    
    // Log contract pressure (dev only)
    if (import.meta.env.MODE !== "production" && agentsWithContracts > 0) {
      console.log('[Contract Pressure] Agents with contracts:', agentsWithContracts);
      console.log('[Contract Pressure] Under pressure:', agentsUnderPressure);
      console.log('[Contract Pressure] Avg urgency:', averageUrgencyScore);
      console.log('[Contract Pressure] At risk:', atRiskContracts);
    }
  }
  
  // Log routing result (dev only)
  if (import.meta.env.MODE !== "production") {
    if (routingResult.outcome === 'success') {
      console.log('[Pool Routing] Winner:', routingResult.selectedAgentName);
      console.log('[Pool Routing] Charged:', routingResult.chargedAmount);
    } else {
      console.log('[Pool Routing] UNSOLD:', routingResult.reason);
    }
  }
  
  return {
    ...routingResult,
    lead,
    timestamp: new Date().toISOString(),
    poolMeta: poolMetaWithIds,
  };
}

/**
 * Quick multi-agent simulation with auto-generated lead
 */
export function quickMultiAgentSimulate(
  leadTypeId: string,
  state: string,
  agents: MockAgent[],
  weights: AllocationWeights,
  context: EligibilityContext = {},
  options?: { 
    usePoolSelection?: boolean; 
    targetPoolSize?: number;
    contractPressures?: Map<string, AgentContractPressure>;
    agentGuardrails?: Map<string, GuardrailInputs>;
  }
): MultiAgentSimulationResult {
  const lead = generateMockLead(leadTypeId, state);
  
  if (options?.usePoolSelection) {
    return simulateWithPoolSelection(
      lead, 
      agents, 
      weights, 
      context, 
      options.targetPoolSize,
      options.contractPressures,
      options.agentGuardrails
    );
  }
  
  return simulateMultiAgentRouting(lead, agents, weights, context);
}

// --- Orchestrated Simulation (Recommended) ---

/**
 * Simulate routing using the full orchestrator pipeline.
 * 
 * This is the RECOMMENDED way to simulate leads as it uses the
 * same deterministic flow as production routing.
 * 
 * Returns both the orchestrator result and a legacy-compatible format.
 */
export function simulateWithOrchestrator(
  lead: IncomingLead,
  agents: MockAgent[],
  weights: AllocationWeights,
  options?: {
    getPricingRule?: (leadTypeId: string, state: string) => { basePrice: number; active: boolean } | null;
    checkIntake?: (vertical: 'life_insurance' | 'mortgage') => string | null;
    agentGuardrails?: Map<string, GuardrailInputs>;
    contractPressures?: Map<string, AgentContractPressure>;
    targetPoolSize?: number;
    debug?: boolean;
  }
): {
  orchestratorResult: OrchestratorResult;
  legacyResult: MultiAgentSimulationResult;
} {
  const result = routeNewLead(lead, agents, weights, options);
  
  // Build legacy-compatible result
  const legacyResult: MultiAgentSimulationResult = result.success
    ? {
        outcome: 'success' as const,
        selectedAgentId: result.deliveryImpact.agentId!,
        selectedAgentName: result.deliveryImpact.agentName!,
        chargedAmount: result.deliveryImpact.chargedAmount!,
        priorityScore: result.observability.priorityScore!,
        breakdown: result.observability.allocationBreakdown!,
        eligibleAgents: result.observability.evaluatedAgents
          .filter(ea => ea.eligible)
          .map(ea => ({
            agent: agents.find(a => a.id === ea.agentId)!,
            effectiveBid: ea.effectiveBid!,
            eligibilityChecks: [],
            breakdown: ea.breakdown!,
          })),
        ineligibleAgents: result.observability.evaluatedAgents
          .filter(ea => !ea.eligible)
          .map(ea => ({
            agent: agents.find(a => a.id === ea.agentId)!,
            eligibilityChecks: [],
            rejectionCode: ea.rejectionCode as any,
            rejectionReason: ea.rejectionReason!,
          })),
        pickAttempts: result.observability.pickAttempts,
        totalAgentsEvaluated: result.observability.totalAgentsEvaluated,
        lead,
        timestamp: new Date(result.observability.completedAt).toISOString(),
        poolMeta: result.observability.poolMeta,
      }
    : {
        outcome: 'unsold' as const,
        reason: result.observability.rejectionReason!,
        code: (result.observability.rejectionCode || 'UNSOLD_LEAD') as any,
        eligibleAgents: result.observability.evaluatedAgents
          .filter(ea => ea.eligible)
          .map(ea => ({
            agent: agents.find(a => a.id === ea.agentId)!,
            effectiveBid: ea.effectiveBid!,
            eligibilityChecks: [],
            breakdown: ea.breakdown!,
          })),
        ineligibleAgents: result.observability.evaluatedAgents
          .filter(ea => !ea.eligible)
          .map(ea => ({
            agent: agents.find(a => a.id === ea.agentId)!,
            eligibilityChecks: [],
            rejectionCode: ea.rejectionCode as any,
            rejectionReason: ea.rejectionReason!,
          })),
        pickAttempts: result.observability.pickAttempts,
        totalAgentsEvaluated: result.observability.totalAgentsEvaluated,
        lead,
        timestamp: new Date(result.observability.completedAt).toISOString(),
        poolMeta: result.observability.poolMeta,
      };

  return { orchestratorResult: result, legacyResult };
}
